import * as React from 'react';
import { cn } from '../../lib/utils';

export interface PageHeaderProps {
  /** Optional action slot (e.g. buttons) rendered on the right */
  action?: React.ReactNode;
  /** Optional back button rendered to the left of the title (only in banner variant) */
  backButton?: React.ReactNode;
  /** Optional breadcrumb rendered above the title (only in banner variant) */
  breadcrumb?: React.ReactNode;
  /** Optional className for the container wrapping the title and description */
  contentClassName?: string;
  /** Optional description/subtitle text displayed below the title */
  description?: string;
  /** Optional className for the description element */
  descriptionClassName?: string;
  /** Optional icon rendered next to the description text */
  icon?: React.ReactNode;
  /** Page title */
  title: string;
  /** Visual variant — "default" is lightweight inline; "banner" has card background and border */
  variant?: 'banner' | 'default';
}

function BannerContent({
  backButton,
  breadcrumb,
  contentClassName,
  description,
  descriptionClassName,
  icon,
  title,
}: Omit<PageHeaderProps, 'action' | 'variant'>): React.JSX.Element {
  return (
    <>
      {breadcrumb}
      <div className={cn('flex flex-col', breadcrumb && 'mt-0.5', contentClassName)}>
        <div className="flex items-center gap-2">
          {backButton}
          <h2 className="text-2xl font-bold text-foreground">{title}</h2>
        </div>
        {description && (
          <p className={cn('flex items-center gap-2 text-muted-foreground', descriptionClassName)}>
            {icon}
            {description}
          </p>
        )}
      </div>
    </>
  );
}

function DefaultContent({
  contentClassName,
  description,
  descriptionClassName,
  icon,
  title,
}: Omit<PageHeaderProps, 'action' | 'variant'>): React.JSX.Element {
  return (
    <div className={cn('space-y-1', contentClassName)}>
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      {description && (
        <p
          className={cn(
            icon ? 'flex items-center gap-2 text-muted-foreground' : 'text-muted-foreground',
            descriptionClassName,
          )}
        >
          {icon}
          {description}
        </p>
      )}
    </div>
  );
}

export function PageHeader({ action, variant = 'default', ...props }: PageHeaderProps): React.JSX.Element {
  const isBanner = variant === 'banner';

  return (
    <header
      className={
        isBanner
          ? 'flex items-center justify-between border-b border-border bg-card px-6 py-4'
          : 'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'
      }
    >
      {isBanner ? (
        <div>
          <BannerContent {...props} />
        </div>
      ) : (
        <DefaultContent {...props} />
      )}
      {action && <div className="flex-none">{action}</div>}
    </header>
  );
}
